import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { showLoader, hideLoader } from 'store/uiSlice';
import { showToast } from 'store/toastSlice';
import { fetchCurrentUser } from 'store/authSlice';
import { authService } from 'api/auth';
import { formatLocalTime } from 'utils/formatDate';
import { SelfExclusionPanel } from './SelfExclusionPanel';
import styles from './generalTab.module.scss';

const GeneralTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { user: rawUser } = useAppSelector((state) => state.auth);
  const user = rawUser as any;

  const [formState, setFormState] = useState({
    username: user?.username || '',
    vipLevel: user?.level?.current?.name || 'Vip 1',
    email: user?.email || '',
    isUsernameHidden: user?.isUsernameHidden || false,
  });

  const signupDate = formatLocalTime(user?.createdAt, {
    includeTime: false,
    locale: i18n.language,
    fallback: 'Feb 5, 2026',
  });

  const isEmailVerified = user?.isEmailVerified || false;
  const [codeError, setCodeError] = useState('');
  const [mailStatus, setMailStatus] = useState('');

  const handleMailVerify = async () => {
    setCodeError('');
    try {
      dispatch(showLoader({ text: t('settings.general.sendingMail') }));
      const res = await authService.resendVerificationEmail();
      if (res.status === 200) {
        setMailStatus(`${res.data.data.message}! ${t('settings.general.checkEmail')}`);
      }
    } catch (err: any) {
      const message = err.response?.data?.message || t('settings.general.invalidCode');
      setCodeError(message);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleTogglePrivacy = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isUsernameHidden = e.target.checked;
    setFormState((prev) => ({ ...prev, isUsernameHidden }));

    try {
      await authService.updatePrivacy({ isUsernameHidden });
      dispatch(fetchCurrentUser(true));
      dispatch(showToast({ text: t('settings.general.privacyUpdated'), type: 'success' }));
    } catch (err) {
      setFormState((prev) => ({ ...prev, isUsernameHidden: !isUsernameHidden }));
      dispatch(showToast({ text: t('settings.general.privacyUpdateFailed'), type: 'error' }));
    }
  };

  return (
    <div className={styles.generalTab}>
      {codeError && <div className={styles.smsError}>{codeError}</div>}
      <h3 className={styles.sectionTitle}>{t('settings.general.personalData')}</h3>
      <div className={styles.gridContainer}>
        <div className={styles.inputGroup}>
          <label>{t('settings.general.username')}</label>
          <input type="text" value={formState.username} readOnly className={styles.readonlyInput} />
        </div>
        <div className={styles.inputGroup}>
          <label>{t('settings.general.level')}</label>
          <div className={styles.vipInputWrapper}>
            <input
              type="text"
              value={formState.vipLevel}
              readOnly
              className={styles.readonlyInput}
            />
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label>{t('settings.general.mail')}</label>
          <div className={styles.actionInputWrapper}>
            <input
              type="email"
              value={formState.email}
              title={formState.email}
              readOnly
              className={`${styles.readonlyInput} ${!isEmailVerified ? styles.hasAction : ''}`}
            />
            {!isEmailVerified && (
              <button onClick={handleMailVerify} className={styles.verifyBtn}>
                {t('settings.general.verify')}
              </button>
            )}
          </div>
          {!mailStatus ? (
            isEmailVerified ? (
              <span className={styles.verifiedText}>{t('settings.general.mailVerified')}</span>
            ) : (
              <span className={styles.unverifiedText}>{t('settings.general.unverifiedEmail')}</span>
            )
          ) : (
            <span className={styles.verifiedText}>{mailStatus}</span>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label>{t('settings.general.memberSince')}</label>
          <input type="text" value={signupDate} readOnly className={styles.readonlyInput} />
        </div>
      </div>
      <div className={styles.switchWrapper}>
        <div className={styles.switchInfo}>
          <span className={styles.switchLabel}>{t('settings.general.hideUsername')}</span>
          <span className={styles.switchDesc}>{t('settings.general.hideUsernameDesc')}</span>
        </div>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={formState.isUsernameHidden}
            onChange={handleTogglePrivacy}
          />
          <span className={styles.slider}></span>
        </label>
      </div>

      <SelfExclusionPanel user={user} />
    </div>
  );
};

export default GeneralTab;
